// Deterministic topic classifier for legal citations.
//
// The report chapters key off the `topic` field to route content:
//   - Chapter 05 (State Law):   topic must NOT contain "regulation"
//   - Chapter 06 (Ownership):   topic/title/summary matched against
//                                abandonment/lien/notice/ownerRights/
//                                vendorObligations/timeline patterns
//   - Chapter 07 (Regulation):  topic MUST contain "regulation"
//
// This classifier rewrites the retrieved `topic` string into canonical,
// lowercase labels so downstream matching is stable across LLM runs. A
// citation can carry multiple labels; we join them with a bar (" | ") which
// still satisfies every chapter's substring check.

export type LegalTopic =
  | "state_law"
  | "regulation"
  | "abandonment"
  | "lien"
  | "notice"
  | "owner_rights"
  | "vendor_obligations"
  | "timeline"
  | "fee_limit"
  | "release"
  | "recordkeeping"
  | "auction_disposition"
  | "municipal_rule"
  | "carrier_guideline";

const TOPIC_PATTERNS: Record<LegalTopic, RegExp> = {
  state_law: /\b(statute|revised statutes?|code|property code|civil code|business.{0,3}code|penal code|act)\b/i,
  regulation: /\b(regulation|regulator|administrative code|admin\.? code|rule\b|rules\b|bulletin|guidance)\b/i,
  abandonment: /\b(abandon|derelict|unclaimed vehicle)\b/i,
  lien: /\b(lien|foreclos|possessory|garagekeeper|encumbrance)\b/i,
  notice: /\b(notice|notif|certified mail|publication|notify)\b/i,
  owner_rights: /\b(owner|redeem|redemption|reclaim|possession|retriev|personal property)\b/i,
  vendor_obligations:
    /\b(vendor|operator|towing company|storage facility|garagekeeper|duty|obligation|responsibility|required to)\b/i,
  timeline: /\b(timeline|deadline|days?|business days?|period|hold|waiting|holding period|within \d+)\b/i,
  fee_limit: /\b(fee schedule|maximum rate|rate cap|non-consent|cap on|maximum charge|price ceiling)\b/i,
  release: /\b(release|releas|surrender|return of vehicle)\b/i,
  recordkeeping: /\b(record ?keeping|retain records|log|register|inventory)\b/i,
  auction_disposition: /\b(auction|sale|dispos|public sale|sell the vehicle)\b/i,
  municipal_rule: /\b(municipal|ordinance|city of|county code|local rule)\b/i,
  carrier_guideline: /\b(carrier|insurer|adjuster|policy guideline|bulletin)\b/i,
};

/** Order matters — earlier entries win when a source matches multiple. */
const PRIORITY: LegalTopic[] = [
  "abandonment",
  "lien",
  "notice",
  "fee_limit",
  "release",
  "auction_disposition",
  "owner_rights",
  "vendor_obligations",
  "timeline",
  "recordkeeping",
  "municipal_rule",
  "carrier_guideline",
  "regulation",
  "state_law",
];

export interface ClassifyInput {
  title?: string | null;
  topic?: string | null;
  summary?: string | null;
  citation?: string | null;
  source?: string | null;
  why_it_matters?: string | null;
  potential_negotiation_impact?: string | null;
}

export interface ClassifyResult {
  /** All topics that fired, in priority order. */
  topics: LegalTopic[];
  /** Canonical `topic` string for persistence (bar-separated). */
  canonicalTopic: string;
  /** True when at least one label was assigned. */
  matched: boolean;
}

/**
 * Classify a citation into canonical topic labels. Never throws; always
 * returns at least one label — falls back to `state_law` when nothing else
 * matches so Chapter 05 still receives the citation.
 */
export function classifyLegalTopics(input: ClassifyInput): ClassifyResult {
  const haystack = [
    input.title,
    input.topic,
    input.summary,
    input.citation,
    input.source,
    input.why_it_matters,
    input.potential_negotiation_impact,
  ]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join(" \n ")
    .toLowerCase();

  const matched: LegalTopic[] = [];
  for (const topic of PRIORITY) {
    if (TOPIC_PATTERNS[topic].test(haystack)) matched.push(topic);
  }

  // If regulation fired, drop state_law — the two chapters filter on
  // whether "regulation" appears in the topic string, so we must not put
  // both on the same citation.
  const hasReg = matched.includes("regulation");
  const filtered = hasReg ? matched.filter((t) => t !== "state_law") : matched;

  // Ensure at least one label so Chapter 05 receives it.
  const finalTopics = filtered.length > 0 ? filtered : (["state_law"] as LegalTopic[]);

  return {
    topics: finalTopics,
    canonicalTopic: finalTopics.join(" | "),
    matched: filtered.length > 0,
  };
}

/** True if the classifier considers this a regulation citation. */
export function isRegulationTopic(topic: string | null | undefined): boolean {
  return typeof topic === "string" && /regulation/i.test(topic);
}

/**
 * Citation patterns that identify an ADMINISTRATIVE/REGULATORY authority
 * (agency rule, administrative code, federal regulation, municipal
 * ordinance, agency bulletin) as opposed to a legislative statute.
 */
const REGULATORY_CITATION =
  /(admin(?:istrative)?\.?\s*code|\bt\.?a\.?c\.?\b|\bc\.?\s*f\.?\s*r\.?\b|\bcfr\b|ordinance|municipal code|county code|\bbulletin\b|agency (?:rule|guidance)|\brule\s*(?:no\.?\s*)?\d)/i;

/**
 * Decide whether a legal authority belongs in Regulation Analysis rather
 * than State Law Analysis. Classification is driven by the CITATION (the
 * authority's legal type), not by a substring of the free-text topic —
 * e.g. "Tex. Occ. Code § 2308" is a statute even though its topic reads
 * "Towing and Storage Regulation".
 */
export function isRegulatoryAuthority(row: {
  citation?: string | null;
  statute?: string | null;
  title?: string | null;
  topic?: string | null;
}): boolean {
  const citation = `${row.citation ?? ""} ${row.statute ?? ""}`.trim();
  if (citation) return REGULATORY_CITATION.test(citation);
  const title = (row.title ?? "").trim();
  if (title) return REGULATORY_CITATION.test(title);
  return isRegulationTopic(row.topic);
}

